import { SOURCE_GAME_OPTIONS } from "domains/accountsBuffPlay/constants/source-options"
import { ExperimentPayloadConfig } from "../../store/types"

// ── payload config registry ────────────────────────────────────────────────
//
// To add a new experiment with a payload: add an entry to EXPERIMENT_PAYLOAD_CONFIGS.
// To add a payload-free experiment (variant name is the contract): add an
// entry with an empty `{}` config — it will appear in the experiment dropdown
// and the modal will render no payload fields for it.

type EarnScreenSectionType =
  | "specialMissions"
  | "topPick"
  | "keepPlayingFeatured"
  | "keepPlaying"
  | "discoverMoreGames"

const DEFAULT_EARN_SCREEN_SECTIONS_ORDER: EarnScreenSectionType[] = [
  "specialMissions",
  "topPick",
  "keepPlayingFeatured",
  "keepPlaying",
  "discoverMoreGames",
]

export const EXPERIMENT_PAYLOAD_CONFIGS: Record<string, ExperimentPayloadConfig> = {
  "game-recommendation": {
    discriminator: "engine",
    discriminatorLabel: "Recommendation Engine",
    paramsKey: "params",
    values: {
      none: { label: "None" },
      installed_packages: { label: "Installed Packages" },
      randomized: { label: "Randomized" },
      utm: {
        label: "UTM",
        params: [
          { key: "alpha", label: "Alpha" },
          { key: "beta", label: "Beta" },
          { key: "retentionWeightD1", label: "Retention Weight D1" },
          { key: "retentionWeightD3", label: "Retention Weight D3" },
          { key: "retentionWeightD7", label: "Retention Weight D7" },
          { key: "retentionWeightD14", label: "Retention Weight D14" },
          { key: "clampMin", label: "Clamp Min" },
          { key: "clampMax", label: "Clamp Max" },
          { key: "boostMultiplier", label: "Boost Multiplier" },
          { key: "usePerGameWeights", label: "Use Per-Game Weights", type: "boolean" as const },
        ],
      },
      "utm-v1.2": {
        label: "UTM v1.2 (Action Matview + SUM)",
        params: [
          { key: "alpha", label: "Alpha" },
          { key: "beta", label: "Beta" },
          { key: "retentionWeightD1", label: "Retention Weight D1" },
          { key: "retentionWeightD3", label: "Retention Weight D3" },
          { key: "retentionWeightD7", label: "Retention Weight D7" },
          { key: "retentionWeightD14", label: "Retention Weight D14" },
          { key: "clampMin", label: "Clamp Min" },
          { key: "clampMax", label: "Clamp Max" },
          { key: "boostMultiplier", label: "Boost Multiplier" },
          { key: "actionWeight", label: "Action Weight" },
        ],
      },
    },
  },
  // Payload-free: backend (games.service.ts) checks variant.name === 'treatment' to
  // serve v2 mission-details UI. Any other variant (including 'control') → v1.
  "mission-details-v2": {},
  // Payload-free: backend (special-missions/slot-feature-flag.service.ts) gates the
  // slot system on variant.name === 'treatment'. Any other variant → legacy missions.
  "special-missions-dynamic-slots": {},
  "earn-screen-sections-order": {
    orderedList: {
      key: "sections",
      label: "Sections Order",
      options: DEFAULT_EARN_SCREEN_SECTIONS_ORDER,
      defaultValue: DEFAULT_EARN_SCREEN_SECTIONS_ORDER,
    },
  },
  // 50/50 test: control = false (current tab order), variant = true (My Games tab first for returning users with 2+ games).
  "my-games-tab-first-if-2-games": { booleanField: { key: "enabled", label: "Enabled", defaultValue: false } },
  "game-installed-popup": { booleanField: { key: "enabled", label: "Enabled", defaultValue: false } },

  // Aggregator games visibility. Backend reads `value.enabledSources` and
  // filters the games listing + the postback handler to that set per variant.
  // Defaults each variant to NATIVE-only so a misconfigured experiment never
  // accidentally turns aggregators on; opt in by ticking sources per variant.
  "aggregator-games-availability": {
    multiSelect: {
      key: "enabledSources",
      label: "Enabled Game Sources",
      options: SOURCE_GAME_OPTIONS.map(s => ({ value: s, label: s })),
      defaultValue: ["NATIVE"],
    },
  },

  // Onboarding survey: controls which questions are shown, their order, and whether each is required.
  "onboarding-survey": {
    surveyQuestions: {
      questions: [
        { key: "excitement", label: "Excitement" },
        { key: "gaming_style", label: "Gaming Style" },
        { key: "favorite_games", label: "Favorite Games" },
        { key: "monthly_spend", label: "Monthly Spend" },
        { key: "referral_source", label: "Referral Source" },
        { key: "gender", label: "Gender" },
        { key: "age", label: "Age" },
      ],
    },
  },
}

export const KNOWN_EXPERIMENTS = Object.keys(EXPERIMENT_PAYLOAD_CONFIGS)
